import { useEffect } from "react"

import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { z } from "zod"

import type { FormColumn } from "../schemas/column-schema"

import { LayoutTemplate } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Spinner } from "@/components/ui/spinner"

import { useAddColumnsMutation } from "../hooks/use-add-columns-mutation"
import { useTemplatesQuery } from "../hooks/use-templates-query"

const createApplyTemplateFormSchema = (t: (key: string) => string) =>
  z.object({
    templateId: z.string().min(1, t("pages.ai_parser.validation.template_select_required")),
  })

type ApplyTemplateDialogProps = {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

export function ApplyTemplateDialog({ isOpen, onOpenChange }: ApplyTemplateDialogProps) {
  const { t } = useTranslation()

  const templatesQuery = useTemplatesQuery()
  const addColumnsMutation = useAddColumnsMutation()

  const isPending = addColumnsMutation.isPending
  const templates = templatesQuery.data || []

  const applyTemplateFormSchema = createApplyTemplateFormSchema(t)
  type ApplyTemplateFormValues = z.infer<typeof applyTemplateFormSchema>

  const form = useForm<ApplyTemplateFormValues>({
    resolver: zodResolver(applyTemplateFormSchema),
    defaultValues: {
      templateId: "",
    },
  })

  const selectedTemplateId = form.watch("templateId")
  const isTemplateSelected = Boolean(selectedTemplateId && selectedTemplateId.length > 0)

  const personalTemplates = templates.filter((template) => template.type === "personal")
  const companyTemplates = templates.filter((template) => template.type === "company")

  const handleSubmit = (data: ApplyTemplateFormValues) => {
    const template = templates.find((template) => template.id === data.templateId)

    if (!template) {
      return
    }

    const columnsToAdd: FormColumn[] = template.columns.map((column) => ({
      name: column.name,
      description: column.description,
      dataType: column.dataType,
      optionTypeId: undefined,
      options: column.options ?? [],
    }))

    addColumnsMutation.mutate(
      { columns: columnsToAdd },
      {
        onSuccess: () => {
          form.reset()
          onOpenChange(false)
        },
      }
    )
  }

  useEffect(() => {
    form.reset()
  }, [isOpen, form])

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t("pages.ai_parser.apply_template_dialog.title")}</DialogTitle>
          <DialogDescription>
            {t("pages.ai_parser.apply_template_dialog.description")}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form className="space-y-4" onSubmit={form.handleSubmit(handleSubmit)}>
            <FormField
              control={form.control}
              name="templateId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("pages.ai_parser.apply_template_dialog.field_title")}</FormLabel>
                  <FormControl>
                    <div className="space-y-2">
                      <Select
                        disabled={isPending || templatesQuery.isLoading}
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue
                            placeholder={t(
                              "pages.ai_parser.apply_template_dialog.select_placeholder"
                            )}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {personalTemplates.length > 0 && (
                            <>
                              <div className="text-muted-foreground px-2 py-1.5 text-xs font-semibold">
                                {t("pages.ai_parser.apply_template_dialog.personal")}
                              </div>
                              {personalTemplates.map((template) => (
                                <SelectItem key={template.id} value={template.id}>
                                  <div className="flex w-full items-center justify-between">
                                    <span>{template.title}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </>
                          )}
                          {companyTemplates.length > 0 && (
                            <>
                              <div className="text-muted-foreground px-2 py-1.5 text-xs font-semibold">
                                {t("pages.ai_parser.apply_template_dialog.company")}
                              </div>
                              {companyTemplates.map((template) => (
                                <SelectItem key={template.id} value={template.id}>
                                  <div className="flex w-full items-center justify-between">
                                    <span>{template.title}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {templatesQuery.isLoading && (
              <div className="flex items-center justify-center py-4">
                <Spinner />
                <span className="text-muted-foreground ml-2 text-sm">
                  {t("pages.ai_parser.apply_template_dialog.loading")}
                </span>
              </div>
            )}

            {templates.length === 0 && !templatesQuery.isLoading && (
              <div className="text-muted-foreground py-4 text-center text-sm">
                {t("pages.ai_parser.apply_template_dialog.no_templates")}
              </div>
            )}

            <DialogFooter>
              <Button disabled={isPending || !isTemplateSelected} type="submit">
                {isPending ? (
                  <>
                    <Spinner />
                    {t("pages.ai_parser.apply_template_dialog.applying")}
                  </>
                ) : (
                  <>
                    <LayoutTemplate className="size-4" />
                    {t("pages.ai_parser.apply_template_dialog.apply")}
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
